// The station sheet's Station client section (F-STN-12, spec/10 §8, spec/13 §3): the Clavian app this PC runs, the
// version it would be offered now, and what its updater is doing. A rolled-back or failed update is a warning with
// the updater's own detail ("health check failed: realtime"); "Not reported" for a PC running an older app. It reads
// the venue's one `GET …/station-clients` list (shared with the Stations list's Client cell) and picks this station.
// With `stations:maintenance` it offers **Update now** (spec/10 §8.2, 2026-10-07) for a PC that has reported: a confirm
// in place (a dialog never opens a dialog, spec/13 §2), then the station's answer in words while the status row follows the download and install. The row
// says where any update is in plain words, with the download's bar (spec/10 §8.3): "Downloading update 1.10.8500 — 45%",
// "Installing — the PC restarts in a moment", "Up to date (1.10.8500)".
//
// A diskless PC (CCBoot, 2026-10-07, spec/10 §14) does not update itself — what it installs is lost when it restarts — so
// its row says "Update the image" and why, and the action is **Update the image** (from a PC started as CCBoot super
// client). The facts say how the PC starts ("Diskless (CCBoot)"), and an image without the player account is a warning.

import type { FloorStationState } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import type { ReactNode } from 'react';

import { useCurrentVenue } from '../shell/scope';
import { bootLabel, ClientProgress, ClientStatusBadge, clientMovement, readClient } from '../stations/client';
import { useStationClients } from '../stations/queries';
import { UpdateNowButton, UpdateNowConfirm, UpdateNowStatus, useUpdateNow } from '../stations/update-now';
import { Facts, Notice, Section } from '../ui';
import { useProblem } from '../api/use-problem';

export function StationClientSection({ station, canUpdate = false }: { readonly station: FloorStationState; readonly canUpdate?: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const formatter = useVenueFormatter();
  const venue = useCurrentVenue();
  const updateNow = useUpdateNow(station);
  const clients = useStationClients(venue.organizationId, venue.id, true, updateNow.following);
  const data = clients.data;
  const client = data?.data.find((row) => row.stationId === station.stationId);
  // Update now goes to a PC the list names — paired, a gaming PC with the app — once it has reported its client.
  const offersUpdate = canUpdate && client !== undefined && client.clientVersion !== null;
  const boot = client?.boot ?? null;
  // A diskless client updates only through its image: the action is "Update the image", and only when one is offered.
  const disklessClient = boot?.kind === 'diskless_client';

  let badge: ReactNode = null;
  let body: ReactNode;
  let action: ReactNode = null;
  if (clients.isError) {
    body = <p className="console-muted">{problem.text(clients.error, { fallback: t('stations.client.failed') })}</p>;
  } else if (data === undefined) {
    body = <p className="console-muted">{t('stations.client.loading')}</p>;
  } else {
    const reading = readClient(client);
    const movement = clientMovement(reading, t, client, (value, options) => formatter.number(value, options));
    badge = <ClientStatusBadge reading={reading} />;
    const none = t('stations.client.none');
    const offersImageUpdate = offersUpdate && disklessClient && reading.status === 'image';
    const offersInstall = offersUpdate && !disklessClient;
    action = offersImageUpdate ? (
      <UpdateNowButton station={station} state={updateNow} reason="update_image" />
    ) : offersInstall ? (
      <UpdateNowButton station={station} state={updateNow} />
    ) : null;
    body =
      reading.status === 'unknown' || client === undefined ? (
        <p className="console-muted">{t('stations.client.neverReported')}</p>
      ) : (
        <>
          {offersImageUpdate || offersInstall ? <UpdateNowConfirm state={updateNow} /> : null}
          {offersImageUpdate || offersInstall ? <UpdateNowStatus station={station} state={updateNow} /> : null}
          {movement === null ? null : <p>{movement}</p>}
          {/* The download's bar while it downloads (2026-10-07, spec/10 §8.3). */}
          <ClientProgress reading={reading} label={movement ?? t('stations.client.title')} className="console-update-now__bar" />
          {disklessClient ? <p className="console-muted">{t('stations.client.image.hint')}</p> : null}
          {boot?.kind === 'diskless_image_maintenance' ? <p className="console-muted">{t('stations.client.image.maintenance')}</p> : null}
          {boot !== null && boot.kind !== 'local' && boot.issues.includes('player_account_missing') ? (
            <Notice tone="warn" title={t('stations.client.boot.playerAccountMissing.title')}>
              <p>{t('stations.client.boot.playerAccountMissing.body')}</p>
            </Notice>
          ) : null}
          {reading.status === 'rolled_back' || reading.status === 'failed' ? (
            <Notice tone="warn" title={t(reading.status === 'rolled_back' ? 'stations.client.rolledBack' : 'stations.client.failedHint')}>
              {client.detail === null ? null : <p>{t('stations.client.detail', { detail: client.detail })}</p>}
            </Notice>
          ) : null}
          <Facts
            columns={3}
            items={[
              { key: 'version', label: t('stations.client.version'), value: <span className="console-num">{client.clientVersion ?? t('stations.client.notReported')}</span> },
              { key: 'kiosk', label: t('stations.client.kiosk'), value: <span className="console-num">{client.kioskVersion ?? none}</span> },
              { key: 'offered', label: t('stations.client.offered'), value: <span className="console-num">{client.targetVersion ?? none}</span> },
              { key: 'channel', label: t('stations.client.channel'), value: t(`stations.client.channels.${data.channel}`) },
              ...(boot === null ? [] : [{ key: 'boot', label: t('stations.client.boot.label'), value: bootLabel(boot, t) }]),
              {
                key: 'reported',
                label: t('stations.client.reported'),
                value: client.reportedAt === null ? none : <span className="console-num">{new Date(client.reportedAt).toLocaleString()}</span>,
              },
            ]}
          />
        </>
      );
  }

  const actions =
    badge === null ? undefined : action !== null ? (
      <span className="console-update-now__head">
        {badge}
        {action}
      </span>
    ) : (
      badge
    );

  return (
    <Section title={t('stations.client.title')} description={t('stations.client.hint')} titleAs="h3" actions={actions}>
      {body}
    </Section>
  );
}
